/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useAncestorPath } from '@squiz/dxp-console-support';
import { Breadcrumbs, BreadcrumbsProps } from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { COMPONENT_SERVICE_STRINGS } from '../../Constants/Strings.constants';
import {
  ComponentManifestResult,
  ComponentServiceGetComponentDto,
  ComponentVersionResult,
  ComponentVersionsResult,
  getComponentService,
} from '../../Services/component';
import { sortVersions } from '../../Utilities/SortVersions/SortVersions';
import { ComponentDetailsProps } from './ComponentDetails.props';
import { ComponentDetailsDescription } from './elements/ComponentDetailsDescription/ComponentDetailsDescription';
import { ComponentDetailsPreview } from './elements/ComponentDetailsPreview/ComponentDetailsPreview';
import { ComponentDetailsSets } from './elements/ComponentDetailsSets/ComponentDetailsSets';
import { ComponentDetailsVersions } from './elements/ComponentDetailsVersions/ComponentDetailsVersions';
import { DeleteAllComponentVersions } from './elements/DeleteAllComponentVersions/DeleteAllComponentVersions';

/**
 * The component details screen.
 * @export
 * @returns {JSX.Element} The `<ComponentDetails />` component.
 */
export const ComponentDetails = (): JSX.Element => {
  const { t } = useTranslation();

  const params = useParams();
  const { componentNamespace, componentName } = params;

  const [componentDetails, setComponentDetails] = useState<ComponentVersionsResult>([null, null]);
  const [componentDetailsVersion, setComponentDetailsVersion] = useState<ComponentVersionResult>([null, null]);
  const [componentManifestVersion, setComponentManifestVersion] = useState<ComponentManifestResult>([null, null]);
  const [availableVersions, setAvailableVersions] = useState<Array<string>>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>(``);

  // Component service
  const componentService = getComponentService();

  // Labels
  const labels = useMemo(() => {
    return {
      allComponents: t(COMPONENT_SERVICE_STRINGS.allComponents),
    };
  }, [t]);

  const updateComponentDetails = useCallback((): void => {
    if (componentNamespace && componentName) {
      // Construct request dto
      const dto: ComponentServiceGetComponentDto = { webpath: `${componentNamespace}/${componentName}` };

      // Send the request to get latest component details
      lastValueFrom(componentService.getComponentVersions(dto))
        .then((res) => {
          return setComponentDetails([res, null]);
        })
        .catch((err) => {
          setComponentDetails([null, null]);
        });
    }
  }, [componentName, componentNamespace, componentService]);

  const updateComponentDetailsVersion = useCallback((): void => {
    if (componentNamespace && componentName && selectedVersion) {
      // Construct request dto
      const dto: ComponentServiceGetComponentDto = {
        version: selectedVersion,
        webpath: `${componentNamespace}/${componentName}`,
      };

      // Send the request to get the component version details
      lastValueFrom(componentService.getComponentVersion(dto))
        .then((res) => {
          return setComponentDetailsVersion([res, null]);
        })
        .catch((err) => {
          setComponentDetailsVersion([null, null]);
        });

      // Send the request to get the latest component version manifest
      lastValueFrom(
        componentService.getComponentManifest({
          name: componentName,
          namespace: componentNamespace,
          version: selectedVersion,
        }),
      )
        .then((res) => {
          return setComponentManifestVersion([res, null]);
        })
        .catch((err) => {
          setComponentManifestVersion([null, null]);
        });
    }
  }, [componentName, componentNamespace, componentService, selectedVersion]);

  // Run when first load
  useEffect(() => {
    updateComponentDetails && updateComponentDetails();
  }, [updateComponentDetails]);

  // Run every time the component version selection is updated
  useEffect(() => {
    updateComponentDetailsVersion && updateComponentDetailsVersion();
  }, [selectedVersion, updateComponentDetailsVersion]);

  // Run when component details has been fetched to see what versions are available
  useEffect(() => {
    if (componentDetails[0]) {
      // We want to to initiate with the latest version
      const componentVersions = sortVersions(
        componentDetails[0].versions.map((version) => version.version),
        'desc',
      );
      setSelectedVersion(componentVersions[0]);
      setAvailableVersions(componentVersions);
    }
  }, [componentDetails]);

  // Render the screen content
  let breadcrumbs: JSX.Element | undefined;
  let screenHeading = '';
  let screenContent = <></>;

  // Prepare breadcrumbs
  const capabilityBreadcrumbs = useCapabilityBreadcrumbs();
  const breadcrumbProps: BreadcrumbsProps = {
    links: [
      ...capabilityBreadcrumbs,
      {
        name: labels.allComponents,
        url: useAncestorPath(false, 2),
      },
    ],
  };

  if (componentDetails[0] && componentDetailsVersion[0]) {
    const { displayName } = componentDetailsVersion[0];

    // Breadcrumbs
    breadcrumbProps.currentPageName = displayName;
    breadcrumbs = <Breadcrumbs {...breadcrumbProps} data-id="component-details-breadcrumbs" />;

    screenHeading = displayName;

    const componentType = componentDetails[0]?.versions.find((v) => v.version === selectedVersion)?.type ?? 'server';

    const customProps: ComponentDetailsProps = {
      componentDetails: componentDetailsVersion[0],
      componentManifest: componentManifestVersion[0],
      componentType: componentType as 'server' | 'edge',
      componentVersions: availableVersions,
      name: componentName,
      namespace: componentNamespace,
      selectedVersion: selectedVersion,
      updateVersion: (version: string) => setSelectedVersion(version),
    };

    screenContent = (
      <>
        <div className="component-details__component-details-container">
          <ComponentDetailsDescription {...customProps} />
          <div className="component-details__component-details-remove-button">
            <DeleteAllComponentVersions
              componentName={componentName ?? ''}
              componentNamespace={componentNamespace ?? ''}
              includedInSets={componentDetails[0].includedInSets}
            />
          </div>
        </div>
        <ComponentDetailsVersions {...customProps} />
        <ComponentDetailsSets {...customProps} />
        <ComponentDetailsPreview {...customProps} />
      </>
    );
  }
  return (
    <DxpScreen breadcrumbs={breadcrumbs} heading={screenHeading}>
      {screenContent}
    </DxpScreen>
  );
};
